import React, { useEffect, useState } from 'react';
import { Phone, Navigation, Star } from 'lucide-react';
import { restaurantInfo, hours } from '../data/mockData';
import { isOpenNow, getNextOpeningTime } from '../utils/timeUtils';
import { trackCTAClick } from '../utils/analytics';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Hero = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openingInfo, setOpeningInfo] = useState('');
  const [reviewStats, setReviewStats] = useState({ total_reviews: 273, average_rating: 4.6 });

  useEffect(() => {
    const updateOpenStatus = () => {
      const open = isOpenNow(hours);
      setIsOpen(open);
      if (!open) {
        setOpeningInfo(getNextOpeningTime(hours));
      }
    };

    updateOpenStatus();
    const interval = setInterval(updateOpenStatus, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchReviewStats = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/settings/reviews`);
        setReviewStats(res.data);
      } catch {
        // keep defaults
      }
    };
    fetchReviewStats();
  }, []);

  const handleCTAClick = (ctaType) => {
    trackCTAClick(ctaType);
  };

  return (
    <section id="accueil" className="relative min-h-screen flex items-center pt-32 overflow-hidden">
      {/* Hero Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1760322328049-91bb196d41d9?w=1920&q=80"
          alt="Delicious Tacos"
          className="w-full h-full object-cover"
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8 text-white">
            {/* Status Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
              <div className={`w-3 h-3 rounded-full ${
                isOpen ? 'bg-green-400 animate-pulse' : 'bg-red-400'
              }`}></div>
              <span className="text-sm font-semibold">
                {isOpen ? 'Ouvert maintenant' : openingInfo}
              </span>
            </div>

            {/* Animated Logo Text */}
            <div className="relative">
              <h1 className="text-7xl lg:text-8xl font-black mb-2 leading-none animate-fade-in" style={{ fontFamily: 'Crimson Text, serif' }}>
                <span className="text-white drop-shadow-2xl">LE</span>
                <br />
                <span className="bg-gradient-to-r from-[#D4AF37] via-[#FFD700] to-[#D4AF37] bg-clip-text text-transparent drop-shadow-2xl animate-pulse">
                  COSMOS
                </span>
                <br />
                <span className="text-white drop-shadow-2xl">TACOS</span>
              </h1>
              {/* Glow Effect */}
              <div className="absolute -inset-4 bg-[#D4AF37]/20 blur-3xl -z-10 animate-pulse"></div>
            </div>

            {/* Location */}
            <p className="text-3xl font-bold text-[#D4AF37]">
              Saint-Étienne
            </p>

            {/* Tagline */}
            <p className="text-2xl text-white/90 leading-relaxed font-light">
              Le goût qui rassemble Saint-Étienne
            </p>

            {/* Rating */}
            <div className="flex items-center space-x-4 bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/20 w-fit" data-testid="hero-rating">
              <div className="flex items-center space-x-1">
                {[...Array(5)].map((_, i) => {
                  const filled = i < Math.floor(reviewStats.average_rating);
                  const half = !filled && i === Math.floor(reviewStats.average_rating) && reviewStats.average_rating % 1 >= 0.3;
                  return half ? (
                    <div key={i} className="relative">
                      <Star size={20} className="text-white/30" />
                      <div className="absolute inset-0 overflow-hidden w-1/2">
                        <Star size={20} className="fill-[#D4AF37] text-[#D4AF37]" />
                      </div>
                    </div>
                  ) : (
                    <Star
                      key={i}
                      size={20}
                      className={filled ? 'fill-[#D4AF37] text-[#D4AF37]' : 'fill-white/30 text-white/30'}
                    />
                  );
                })}
              </div>
              <div className="border-l-2 border-white/20 pl-4">
                <div className="text-xl font-bold">{reviewStats.average_rating}/5</div>
                <div className="text-xs text-white/70">{reviewStats.total_reviews} avis</div>
              </div>
            </div>

            {/* Delivery Banner */}
            <div className="bg-[#D4AF37] text-black px-6 py-3 rounded-xl font-bold text-lg inline-block">
              🎉 Livraison gratuite dès {restaurantInfo.freeDeliveryFrom}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <a
                href={restaurantInfo.deliveryLinks.uberEats}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleCTAClick('order_uber')}
                className="group relative px-8 py-4 bg-white text-black font-bold rounded-xl overflow-hidden transform hover:scale-105 transition-all duration-300"
              >
                <span className="relative z-10">Commander Uber Eats</span>
                <div className="absolute inset-0 bg-[#D4AF37] transform translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
              </a>
              <a
                href={restaurantInfo.deliveryLinks.deliveroo}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleCTAClick('order_deliveroo')}
                className="group relative px-8 py-4 bg-white text-black font-bold rounded-xl overflow-hidden transform hover:scale-105 transition-all duration-300"
              >
                <span className="relative z-10">Commander Deliveroo</span>
                <div className="absolute inset-0 bg-[#D4AF37] transform translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
              </a>
            </div>

            <div className="flex flex-wrap gap-4">
              <a
                href={`tel:${restaurantInfo.phone1.replace(/\s/g, '')}`}
                onClick={() => handleCTAClick('call')}
                className="flex items-center justify-center space-x-2 bg-[#D4AF37] text-black px-8 py-4 rounded-xl font-bold hover:bg-[#FFD700] transition-all duration-200 transform hover:scale-105"
              >
                <Phone size={20} />
                <span>Appeler</span>
              </a>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurantInfo.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleCTAClick('directions')}
                className="flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-xl font-bold border border-white/20 hover:bg-white/20 transition-all duration-200"
              >
                <Navigation size={20} />
                <span>Itinéraire</span>
              </a>
            </div>
          </div>

          {/* Right Content - Feature Cards */}
          <div className="hidden lg:grid grid-cols-2 gap-6">
            {[
              { img: "https://customer-assets.emergentagent.com/job_cosmos-tacos/artifacts/xz6v0jee_Tacos.jpg", title: "Tacos XXL" },
              { img: "https://images.unsplash.com/photo-1550547660-d9450f859349?w=500&q=80", title: "Burgers" },
              { img: "https://customer-assets.emergentagent.com/job_cosmos-tacos/artifacts/7dq2r8c6_Roul%C3%A9s.jpg", title: "Roulés" },
              { img: "https://customer-assets.emergentagent.com/job_cosmos-tacos/artifacts/c3bt2wzk_French%20Fries.jpg", title: "Frites" }
            ].map((item, index) => (
              <div
                key={index}
                className="group relative h-48 rounded-2xl overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-300"
              >
                <img
                  src={item.img}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-xl font-bold">{item.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
